<template>
    <div v-if="additionals">
        <div v-if="additionals.salaVacinacaoLogada && !!additionals.salaVacinacaoLogada.id">
            <rkm-data-table
                :fields="fields"
                :repository="repositories.paciente"
                :request-params="requestParams"
                :disabled-button-search="true"
                :tbody-tr-class="rowClass"
                class="tabela-paciente"
                ref="RkmDataTable"
                sort-by="nome"
            >
                <template v-slot:cell(nome)="{ item }">
                    {{ item.nome }}

                    <template v-if="item.nomeSocial">
                        <template v-if="item.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                            <strong>({{ item.nomeSocial }})</strong>
                        </template>

                        <template v-else>
                            ({{ item.nomeSocial }})
                        </template>
                    </template>

                    <span
                        v-if="item.obitoEm"
                        class="ml-1"
                        title="Paciente em óbito"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="cross" />
                    </span>

                    <span
                        v-if="item.bloqueado"
                        class="ml-1"
                        title="Cadastro bloqueado"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="ban" />
                    </span>

                    <span
                        v-if="isProvisorioOuTemporario(item)"
                        class="ml-1"
                        title="Paciente provisório/temporário"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="hourglass-half" />
                    </span>

                    <span
                        v-if="item.classificacaoId == enums.classificacao.OUTRO_MUNICIPIO"
                        class="ml-1"
                        title="Paciente não munícipe"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="map-pin" />
                    </span>

                    <span
                        v-if="!item.cpf && !item.cns"
                        class="ml-1"
                        title="Paciente sem CPF e CNS"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="exclamation-triangle" />
                    </span>

                    <span
                        v-if="item.pacienteResponsavelFamiliar"
                        class="ml-1"
                        title="Responsável familiar"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="crown" />
                    </span>

                    <span
                        v-if="isIdoso(item.dataNascimento)"
                        class="ml-1"
                        title="Idoso"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="blind" />
                    </span>

                    <span
                        v-if="hasTea(item)"
                        class="ml-1"
                        title="Pessoa com Transtorno do Espectro Autista - TEA"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="infinity" />
                    </span>

                    <span
                        v-if="hasPcd(item)"
                        class="ml-1"
                        title="Pessoa com Deficiência - PCD"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="wheelchair" />
                    </span>
                </template>

                <template v-slot:cell(dataNascimento)="row">
                    {{ formatDate(row.value) }}
                </template>

                <template v-slot:cell(cpf)="row">
                    {{ formatCpf(row.value) }}
                </template>

                <template v-slot:cell(cns)="row">
                    {{ formatCns(row.value) }}
                </template>

                <template v-slot:cell(actions)="row">
                    <rkm-button
                        @click="handleEdit(row.item)"
                        authorize="store-imunizacao-registro-vacinacao"
                        color="primary"
                        small
                        title="Visualizar"
                    >
                        <rkm-icon icon="search" />
                    </rkm-button>
                </template>
            </rkm-data-table>

            <rkm-row class="mt-3">
                <rkm-col>
                    <p class="m-0">
                        <span class="legend-color purple" /> Paciente em óbito<br>
                        <span class="legend-color danger" /> Cadastro bloqueado<br>
                        <span class="legend-color orange" /> Paciente provisório ou temporário<br>
                        <span class="legend-color brown" /> Paciente não munícipe<br>
                        <span class="legend-color warning" /> Paciente sem CPF e CNS
                    </p>
                </rkm-col>
            </rkm-row>
        </div>

        <rkm-alert v-else color="warning">
            A unidade logada não é uma sala de vacinação, favor cadastrá-la para efetuar um registro de vacinação.
        </rkm-alert>
    </div>
</template>

<script>
// Enums.
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import classificacao from '@/enums/cadastro/paciente/classificacao'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

// Mixins.
import { error, formats, permissao, repository, stringCase, certificadoDigital } from '@/mixins'

// Moment.
import moment from 'moment'

// Vuex.
import { mapActions } from 'vuex'

export default {
    computed: {
        fields() {
            const fields = [
                { key: 'nome', label: 'Nome', name: 'cadastroCidadao.nome', searchable: true, sortable: true },
                { key: 'dataNascimento', label: 'Data de nascimento', name: 'cadastroCidadao.dataNascimento', searchable: true, sortable: true },
                { key: 'nomeMae', label: 'Nome da mãe', name: 'cadastroCidadao.nomeMae', searchable: true, sortable: true },
                { key: 'prontuario', label: 'Prontuário municipal', name: 'cadastroPaciente.prontuario', searchable: true, sortable: true },
                { key: 'cpf', label: 'CPF', name: 'cadastroCidadao.cpf', searchable: true, sortable: true },
                { key: 'cns', label: 'CNS', name: 'cadastroCidadao.cns', searchable: true, sortable: true },
            ]

            if (this.verificarPermissao('create-imunizacao-registro-vacinacao')) {
                fields.push({ class: 'col-min text-center', key: 'actions', label: 'Ações' })

            }

            return fields
        },
        requestParams() {

            return { with_paciente_bloqueado: true,  with_paciente_provisorio: true }
        },
    },
    created() {
        this.repositories = {
            paciente: this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente'),
            registroVacinacao: this.getConectaSusRepository('imunizacao.registroVacinacao'),
        }
    },
    data: () => ({
        additionals: null,
        enums: { preferenciaNome, subclassificacao, classificacao, deficienciaBase, deficiencia },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.registroVacinacao.index()
                .then(response => {
                    this.additionals = Object.assign(response.data.additionals)

                    this.exibirAvisoValidadeCertificadoDigital(this.additionals.dadosCertificadoDigital)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleEdit(item) {

            this.setLoading()

            this.repositories.paciente.validCNS(this.camelObjectKeysToSnakeRecursive({pacienteId: item.id}))
                .then(response => {

                    if (!response.data.additionals.cnsValido && response.data.additionals.avisaCnsTemporarioInvalido) {

                        this.showWarningNotification('Paciente com CNS inválido. Registro não permitido.')
                        return false
                    }

                    if (item.subclassificacaoId === this.enums.subclassificacao.TEMPORARIO
                        && response.data.additionals.avisaCnsTemporarioInvalido) {

                            const confirm = async () => {

                                this.$nextTick().then(() => this.irParaRegistro(item))

                                return false
                            }

                            const msg = `Paciente com cadastro temporário. Deseja continuar?`

                            this.showConfirmation(msg, confirm)

                            return false
                    }

                    if (item.obitoEm) {

                            const confirm = async () => {

                                this.$nextTick().then(() => this.irParaRegistro(item))

                                return false
                            }

                            const msg = `Paciente com óbito registrado. Deseja continuar?`

                            this.showConfirmation(msg, confirm)

                            return false
                    }

                    if (item.bloqueado) {

                        const confirm = async () => {

                            this.$nextTick().then(() => this.irParaRegistro(item))

                            return false
                        }

                        if (this.additionals.parametrizacao.cadastroBloqueado) {

                            const msg = `Paciente está com o seu cadastro bloqueado. Deseja continuar?`
                            this.showConfirmation(msg, confirm)
                        } else {

                            const msg = `Paciente está com o seu cadastro bloqueado. O registro da aplicação não
                                será possível, apenas a atualização da carteirinha. Para realizar o registro, é
                                necessário desbloquear o cadastro. Deseja continuar para a tela de registro?`
                            this.showConfirmation(msg, confirm)
                        }

                        return false
                    }

                    if (item.classificacaoId == this.enums.classificacao.PROVISORIO) {

                        const confirm = async () => {

                            this.$nextTick().then(() => this.irParaRegistro(item))

                            return false
                        }

                        if (item.diasCadastroDesatualizado > 0) {

                            if (this.additionals.parametrizacao.cadastroProvisorio) {

                                let msg = `Paciente está com o seu cadastro "classificação provisória" e com cadastro desatualizado há
                                    <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Deseja continuar?`

                                if (item.enderecoDesatualizadoBloqueado) {

                                    msg = `Paciente está com o seu cadastro "classificação provisória" e com cadastro desatualizado há
                                        <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Endereço do usuário também
                                        deve ser atualizado. Deseja continuar?`
                                }

                                this.showConfirmation(msg, confirm)
                            } else {

                                let msg = `Paciente está com o seu cadastro "classificação provisória" e com cadastro desatualizado há
                                    <strong>${item.diasCadastroDesatualizado}</strong> dia(s). O registro da aplicação não
                                    será possível, apenas a atualização da carteirinha. Para realizar o registro, é
                                    necessário atualizar o cadastro e ajustar a classificação do paciente.
                                    Deseja continuar para a tela de registro?`

                                if (item.enderecoDesatualizadoBloqueado) {

                                    msg = `Paciente está com o seu cadastro "classificação provisória" e com cadastro desatualizado há
                                        <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Endereço do usuário também
                                        deve ser atualizado. O registro da aplicação não será possível, apenas a atualização
                                        da carteirinha. Para realizar o registro, é necessário atualizar o cadastro e ajustar
                                        a classificação do paciente. Deseja continuar para a tela de registro?`
                                }

                                this.showConfirmation(msg, confirm)
                            }
                        } else {

                            if (this.additionals.parametrizacao.cadastroProvisorio) {

                                const msg = `Paciente está com o seu cadastro "classificação provisória". Deseja continuar?`
                                this.showConfirmation(msg, confirm)
                            } else {

                                const msg = `Paciente está com o seu cadastro "classificação provisória". O registro da
                                    aplicação não será possível, apenas a atualização da carteirinha. Para realizar o
                                    registro, é necessário atualizar o cadastro e ajustar a classificação do paciente.
                                    Deseja continuar para a tela de registro?`
                                this.showConfirmation(msg, confirm)
                            }
                        }

                        return false
                    }

                    if (item.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {

                        const confirm = async () => {

                            this.$nextTick().then(() => this.irParaRegistro(item))

                            return false
                        }

                        if (item.diasCadastroDesatualizado > 0) {

                            if (this.additionals.parametrizacao.cadastroOutroMunicipio) {

                                let msg = `Paciente está com o seu cadastro "outro município" e com cadastro desatualizado há
                                    <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Deseja continuar?`

                                if (item.enderecoDesatualizadoBloqueado) {

                                    msg = `Paciente está com o seu cadastro "outro município" e com cadastro desatualizado há
                                        <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Endereço do usuário também
                                        deve ser atualizado. Deseja continuar?`
                                }

                                this.showConfirmation(msg, confirm)
                            } else {

                                let msg = `Paciente está com o seu cadastro "outro município" e com cadastro desatualizado há
                                    <strong>${item.diasCadastroDesatualizado}</strong> dia(s). O registro da aplicação não
                                    será possível, apenas a atualização da carteirinha. Para realizar o registro, é
                                    necessário atualizar o cadastro indicando que o paciente pertence a este município.
                                    Deseja continuar para a tela de registro?`

                                if (item.enderecoDesatualizadoBloqueado) {

                                    msg = `Paciente está com o seu cadastro "outro município" e com cadastro desatualizado há
                                        <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Endereço do usuário também
                                        deve ser atualizado. O registro da aplicação não será possível, apenas a atualização
                                        da carteirinha. Para realizar o registro, é necessário atualizar o cadastro indicando
                                        que o paciente pertence a este município. Deseja continuar para a tela de registro?`
                                }

                                this.showConfirmation(msg, confirm)
                            }
                        } else {

                            if (this.additionals.parametrizacao.cadastroOutroMunicipio) {

                                const msg = `Paciente está com o seu cadastro "outro município". Deseja continuar?`
                                this.showConfirmation(msg, confirm)
                            } else {

                                const msg = `Paciente está com o seu cadastro "outro município". O registro da aplicação não
                                    será possível, apenas a atualização da carteirinha. Para realizar o registro, é
                                    necessário atualizar o cadastro indicando que o paciente pertence a este município.
                                    Deseja continuar para a tela de registro?`
                                this.showConfirmation(msg, confirm)
                            }
                        }

                        return false
                    }

                    if (item.diasCadastroDesatualizado > 0) {

                        const confirm = async () => {

                            this.$nextTick().then(() => this.irParaRegistro(item))

                            return false
                        }

                        if (this.additionals.parametrizacao.cadastroOutroMunicipio) {

                            let msg = `Paciente está com o seu cadastro desatualizado há
                                <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Deseja continuar?`

                            if (item.enderecoDesatualizadoBloqueado) {

                                msg = `Paciente está com o seu cadastro desatualizado há
                                    <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Endereço do usuário também deve
                                    ser atualizado. Deseja continuar?`
                            }

                            this.showConfirmation(msg, confirm)
                        } else {

                            let msg = `Paciente está com o seu cadastro desatualizado há <strong>${item.diasCadastroDesatualizado}</strong>
                                dia(s). O registro da aplicação não será possível, apenas a atualização da carteirinha.
                                Para realizar o registro, é necessário atualizar o cadastro. Deseja continuar para a
                                tela de registro?`

                            if (item.enderecoDesatualizadoBloqueado) {

                                msg = `Paciente está com o seu cadastro desatualizado há <strong>${item.diasCadastroDesatualizado}</strong>
                                    dia(s). Endereço do usuário também deve ser atualizado. O registro da aplicação não será
                                    possível, apenas a atualização da carteirinha. Para realizar o registro, é necessário
                                    atualizar o cadastro. Deseja continuar para a tela de registro?`
                            }

                            this.showConfirmation(msg, confirm)
                        }

                        return false
                    }

                    this.irParaRegistro(item)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        irParaRegistro(item) {

            if (item.enderecoDesatualizadoBloqueado) {

                this.showAlert(
                    'O endereço do paciente ainda não foi atualizado pela Base Nacional. Atualize o cadastro para continuar.',
                    'Atenção'
                )

                return
            }

            this.$router.push({ name: 'imunizacao-registro-vacinacao-create', params: { id: item.id } })
        },
        isProvisorioOuTemporario(item) {

            return item.classificacaoId == this.enums.classificacao.PROVISORIO
                || item.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO
        },
        isIdoso(dataNascimento) {

            return !!dataNascimento && moment().diff(moment(dataNascimento), 'years') >= 60
        },
        getDeficienciasBaseIds(item) {

            return item.deficienciasBaseIds
                ? item.deficienciasBaseIds.split(',').map(Number)
                : []
        },
        hasTea(item) {

            return this.getDeficienciasBaseIds(item).includes(this.enums.deficienciaBase.TEA)
                || item.deficienciaId === this.enums.deficiencia.AUTISMO
        },
        hasPcd(item) {

            return this.getDeficienciasBaseIds(item).some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!item.deficienciaId && item.deficienciaId !== this.enums.deficiencia.AUTISMO)
        },
        rowClass(item, type) {
            if(item && type === 'row') {
                if (item.obitoEm) {
                    return 'table-purple'
                }
                if (item.bloqueado) {
                    return 'table-danger'
                }
                if (this.isProvisorioOuTemporario(item)) {
                    return 'table-orange'
                }
                if (item.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {
                    return 'table-brown'
                }
                if (!item.cpf && !item.cns) {
                    return 'table-warning'
                }
            }
        },
    },
    mixins: [ error, formats, permissao, repository, stringCase, certificadoDigital ],
    mounted() {
        this.load()
    },
}
</script>

<style scoped>
.legend-color {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    margin-right: 4px;
}
.legend-color.purple {
    background-color: #6f42c1;
}
.legend-color.danger {
    background-color: var(--danger);
}
.legend-color.orange {
    background-color: #F28C28;
}
.legend-color.brown {
    background-color: #6F4E37;
}
.legend-color.warning {
    background-color: var(--warning);
}

.tabela-paciente >>> .table-orange {
    border-color: #F28C28;
    background-color: #FFE0B2;
}

.tabela-paciente >>> .table-brown {
    border-color: #6F4E37;
    background-color: #D2B48C;
}

.tabela-paciente >>> .table-purple {
    border-color: #6f42c1;
    background-color: #E1D4F2;
}
</style>